import { Box, Card, Typography, IconButton, Collapse, Avatar } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useState } from "react";

import ReminderEditor, { ReminderEditorValue } from "../atoms/ReminderEditor";

import ReminderRow from "../atoms/ReminderRow";

import { colors } from "../../../ui/colors";

import { EReminderProcessType, EReminderTargetEntity } from "../../enums/reminders.enums";

import { CreateRemindersConfig } from "../../interfaces/create-reminder.interface";

import { Reminder } from "../../interfaces/IRemiders";

import { UpdateReminderConfig } from "../../interfaces/update-reminder.interface";

interface Props {
  title: string;

  icon: React.ReactNode;

  reminders: Reminder[];

  processType: EReminderProcessType;

  targetEntity: EReminderTargetEntity;

  onCreateReminder: (payload: CreateRemindersConfig) => void;

  onUpdateReminder: (uid: string, data: UpdateReminderConfig) => void;

  onDeleteReminder: (uid: string) => void;
}

export default function ReminderCategoryCard({
  title,

  icon,

  reminders,

  processType,

  targetEntity,

  onUpdateReminder,

  onCreateReminder,

  onDeleteReminder,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  const [creating, setCreating] = useState(false);

  const handleSave = (reminderData: ReminderEditorValue) => {
    const payload: CreateRemindersConfig = {
      processType,

      targetEntity,

      name: reminderData.name,

      scheduleConfig: {
        type: reminderData.scheduleConfig.type,

        hour: reminderData.scheduleConfig.hour,

        minute: reminderData.scheduleConfig.minute,
      },

      sendEmail: reminderData.sendEmail,

      sendSms: reminderData.sendSms,

      isActive: reminderData.isEnabled,
    };

    onCreateReminder(payload);

    setCreating(false);
  };

  const handleEdit = (uid: string, reminderData: ReminderEditorValue) => {
    const payload: UpdateReminderConfig = {
      processType,

      targetEntity,

      name: reminderData.name,

      scheduleConfig: {
        type: reminderData.scheduleConfig.type,

        hour: reminderData.scheduleConfig.hour,

        minute: reminderData.scheduleConfig.minute,
      },

      sendEmail: reminderData.sendEmail,

      sendSms: reminderData.sendSms,

      isActive: reminderData.isEnabled,
    };

    onUpdateReminder(uid, payload);

    setCreating(false);
  };

  const handleDelete = (uid: string) => {
    onDeleteReminder(uid);
  };

  const handleToggle = (uid: string, nextIsActive: boolean) => {
    onUpdateReminder(uid, {
      isActive: nextIsActive,
    });
  };

  return (
    <Card
      sx={{
        py: 1,

        mt: 2,

        boxShadow: "none",

        borderRadius: "12px",

        border: "1px solid",

        borderColor: colors.gray[200],

        background: colors.gray[50],
      }}
    >
      <Box display="flex" paddingX={2} alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar
            sx={{
              width: 40,

              height: 40,

              fontSize: 20,

              color: colors.orange.base,

              backgroundColor: colors.orange[50],
            }}
          >
            {icon}
          </Avatar>

          <Box>
            <Typography sx={{ fontSize: 16, lineHeight: 1, mb: 0.4 }} fontWeight={600}>
              {title}
            </Typography>

            <Typography
              fontWeight={600}
              sx={{
                fontSize: 10,

                lineHeight: 1,

                mt: 0,

                color: colors.orange.base,
              }}
            >
              {reminders.filter((reminder) => reminder.isActive).length} Active Reminders
            </Typography>
          </Box>
        </Box>

        {!creating && (
          <Box>
            <IconButton
              sx={{
                width: 30,

                height: 30,

                color: "white",

                mr: 1,

                backgroundColor: colors.orange.base,

                "&:hover": { backgroundColor: colors.orange[600] },
              }}
              onClick={() => {
                setCreating(true);

                setExpanded(true);
              }}
            >
              <AddIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <IconButton
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",

                transition: "0.2s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <Collapse in={creating} timeout="auto" unmountOnExit>
        <Box px={2} py={1} sx={{ overflow: "visible" }}>
          <ReminderEditor onCancel={() => setCreating(false)} onSave={handleSave} />
        </Box>
      </Collapse>

      <Collapse in={expanded && !creating}>
        <Box px={2} mt={1}>
          {reminders.map((reminder) => (
            <ReminderRow key={reminder.uid} reminder={reminder} onToggle={handleToggle} onSave={handleEdit} onDelete={handleDelete} />
          ))}
        </Box>
      </Collapse>
    </Card>
  );
}
